import React from "react";
import {
  Typography,
  Stack,
  Button,
  Box,
  Tab,
  Avatar,
  OutlinedInput,
  InputAdornment,
  Autocomplete,
  InputLabel,
  TextField,
  Select,
  MenuItem,
} from "@mui/material";
import Panel from "../Panel/panel";
import PanelHeader, { PanelHeaderProps } from "../PanelHeader/panel-header";
import PanelTabs from "../PanelTabs";
import PanelTabContent from "../PanelTabContent/panel-tab-content";
import SearchIcon from "@mui/icons-material/Search";
import CopyPermissionPopperRole from "../../Misc/CopyPermissionPopper/copy-permission-role-popper";
import CopyPermissionPopperGroup from "../../Misc/CopyPermissionPopper/copy-permission-group-popper";
import PermissionAccordionList from "../../Lists/PermissionsAccordionList/permission-accordion-list";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import {
  cacheService,
  ingestService,
  orderService,
  planningUserTaskService,
  userService,
} from "../../../Core/Api/services";
import { UserAdvancedInfos } from "../../../Core/Api/Types/user-advanced-infos";
import { AclTypes } from "../../../Core/Api/Types/acl-types";
import { CreateGroupRequest } from "../../../Core/Api/Types/create-group-request";
import { DbUserGroupInfos } from "../../../Core/Api/Types/db-user-group-infos";
import { DbLabProviderInfos } from "../../../Core/Api/Types/db-lab-provider-infos";
import { CurrencyTypes } from "../../../Core/Api/Types/currency-types";
import { AclWorkflowAccessLevel } from "../../../Core/Api/Types/acl-workflow-access-level";
import { AclAssetAccessLevel } from "../../../Core/Api/Types/acl-asset-access-level";
import { DbUserGroupEntityInfos } from "../../../Core/Api/Types/db-user-group-entity-infos";
import FormSection from "../../Forms/FormSection/form-section";
import { ObjectClass } from "../../../Core/Api/Types/object-class";
import { produce, immerable } from "immer";
import { ObjectLayer } from "../../../Core/Api/Types/object-layer";
import ModalGroupUserPanelTitleIdAssociation, {
  ModalGroupUserPanelTitleIdAssociationElementProps,
} from "./modal-group-user-panel-title-id-association";
import { ModalContext } from "../../../Components/Modals";
import { GroupPermissionsRows } from "../../../Pages/admin/Settings/SettingsRolesAndPermissions/settings-roles-and-permissions";
import UserGroupChip from "../../Misc/Chips/UserGroupChip/user-group-chip";
import useCustomSnackbar from "../../../Utils/Hooks/useCustomSnackbar";
import AddIcon from "@mui/icons-material/Add";
import ModalCreateOrUpdateResourceView, {
  ModalCreateOrUpdateResourceViewElementProps,
} from "../../Modals/ModalCreateOrUpdateResourceView/mocal-create-or-update-resource-view";
import { DbOrderManualTaskPlanningResourceInfos } from "../../../Core/Api/Types/db-order-manual-task-planning-resource-infos";
import { GroupPlanningViewInfos } from "../../../Core/Api/Types/group-planning-view-infos";
import ResourceViewCard from "../../Cards/ResourceViewCard/resource-view-card";
import ModalCreateOrUpdatePlanningUserGroup, {
  ModalCreateOrUpdatePlanningUserGroupElementProps,
} from "../../Modals/ModalCreateOrUpdatePlanningUserGroup/modal-create-or-update-planning-user-group";
import { GroupPlanningUserGroupInfos } from "../../../Core/Api/Types/group-planning-user-group-infos";
import PlanningUserGroupCard from "../../Cards/PlanningUserGroupsCard/planning-user-group-card";
import ModalSearchQueryBuilder, {
  ModalSearchQueryBuilderElementProps,
} from "../../Modals/ModalSearchQueryBuilder/modal-search-query-builder";
import { DbUserGroupRoleInfos } from "../../../Core/Api/Types/db-user-group-role-infos";
import ConfigGroupAssociations from "../../Config/ConfigGroupAssociations/config-group-associations";
import ConfigContextLevelSettings from "../../Config/ConfigContextLevelSettings/config-context-level-settings";
import ConfigFiltering from "../../Config/ConfigFiltering/config-filtering";
import ConfigMediaShareInfos from "../../Config/ConfigMediaShareInfos/config-media-share-infos";
import { useTranslation } from "react-i18next";
import { minutesToTimeSpan } from "../../../Utils/helpers";

export interface GroupUserPanelProps {
  width?: number;
  open: boolean;
  groupRow?: GroupPermissionsRows;
  groupsNamesAndIds: Record<number, string>;
  roles: DbUserGroupRoleInfos[];
  onClose?: React.MouseEventHandler;
  onConfirm?: (group: DbUserGroupInfos) => void;
}

const GroupUserPanel: React.FC<GroupUserPanelProps> = (props) => {
  const { t } = useTranslation();
  const { groupRow, roles, onConfirm, onClose, open, groupsNamesAndIds } = props;
  const [value, setValue] = React.useState(0);
  const [permissionAnchorRole, setPermissionAnchorRole] = React.useState<HTMLButtonElement>();
  const [permissionAnchorGroup, setPermissionAnchorGroup] = React.useState<HTMLButtonElement>();
  const [filterValue, setFilterValue] = React.useState<string>("");
  const [users, setUsers] = React.useState<UserAdvancedInfos[]>([]);
  const [acls, setAcls] = React.useState<AclTypes[]>([]);
  const [groupInfos, setGroupInfos] = React.useState<DbUserGroupInfos>();
  const [labProviders, setLabProviders] = React.useState<DbLabProviderInfos[] | null>();
  const [currencies, setCurrencies] = React.useState<CurrencyTypes[] | null>();
  const [entities, setEntities] = React.useState<DbUserGroupEntityInfos[] | null>([]);
  const [titleIds, setTitleIds] = React.useState<number[]>([]);
  const [searchValue, setSearchValue] = React.useState<string>("");
  const [availablePlanningResources, setAvailablePlanningResources] = React.useState<
    DbOrderManualTaskPlanningResourceInfos[]
  >([]);

  const enqueueCustomSnackbar = useCustomSnackbar();

  const modalContext = React.useContext(ModalContext);
  const { openModal } = { ...modalContext };

  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    setValue(newValue);
  };

  const onClickCopyPermissionsRole: React.MouseEventHandler<HTMLButtonElement> = (e) => {
    setPermissionAnchorRole((prevState) => (prevState ? undefined : e.currentTarget));
  };

  const onClickCopyPermissionsGroup: React.MouseEventHandler<HTMLButtonElement> = (e) => {
    setPermissionAnchorGroup((prevState) => (prevState ? undefined : e.currentTarget));
  };

  const onClickSaveChanges = (event: React.SyntheticEvent) => {
    if (groupInfos !== groupRow?.groupFull.group || labProviders !== groupRow!.groupFull.labProviders) {
      let group: DbUserGroupInfos = {
        [immerable]: true,
        aclAssetAccessLevel: groupInfos!.aclAssetAccessLevel,
        acls: acls,
        aclWorkflowAccessLevel: groupInfos!.aclWorkflowAccessLevel,
        allowedObjectClasses: groupInfos!.allowedObjectClasses,
        filterTitleIds: titleIds,
        currency: groupInfos!.currency,
        customerIds: groupInfos!.customerIds,
        customFilters: groupInfos!.customFilters,
        defaultShareViewClipsDuration: groupInfos!.defaultShareViewClipsDuration,
        defaultShareName: groupInfos!.defaultShareName,
        defaultShareViewClipsNumber: groupInfos!.defaultShareViewClipsNumber,
        defaultShareViewDays: groupInfos!.defaultShareViewDays,
        defaultShareViewIPs: groupInfos!.defaultShareViewIPs,
        defaultShareViews: groupInfos!.defaultShareViews,
        description: groupInfos!.description,
        enabled: true,
        entityId: groupInfos!.entityId,
        filterContents: groupInfos!.filterContents,
        filterTitleBusinessUnits: groupInfos!.filterTitleBusinessUnits,
        id: groupInfos!.id,
        isNew: true,
        labProviderIds: groupInfos!.labProviderIds,
        maxShareViewClipsDuration: groupInfos!.maxShareViewClipsDuration,
        maxShareViewClipsNumber: groupInfos!.maxShareViewClipsNumber,
        maxShareViewDays: groupInfos!.maxShareViewDays,
        maxShareViewIPs: groupInfos!.maxShareViewIPs,
        maxShareViews: groupInfos!.maxShareViews,
        name: groupInfos!.name,
        platformId: groupInfos!.platformId,
        roleId: groupInfos!.roleId,
        planningInfos: groupInfos!.planningInfos,
      };
      const request: CreateGroupRequest = {
        group: group,
        labProviders: labProviders?.filter((x) => groupInfos?.labProviderIds?.includes(x.id)) || [],
      };
      userService.UpdateGroup(request).then(function (response) {
        if (response.success) {
          enqueueCustomSnackbar(t("alert:success.groupUpdated"), "success");
          onConfirm && onConfirm(group);
        } else enqueueCustomSnackbar(response.message || t("alert:error.anErrorOccurred"), "error");
      });
    }
  };

  const handleOpenModalTitleAssociation = React.useCallback(() => {
    if (!openModal) return;

    openModal<ModalGroupUserPanelTitleIdAssociationElementProps>(ModalGroupUserPanelTitleIdAssociation, {
      titleIds,
      setTitleIds,
    });
  }, [openModal, titleIds]);

  React.useEffect(() => {
    setGroupInfos(groupRow?.groupFull.group!);
    setTitleIds(groupRow?.groupFull.group?.filterTitleIds || []);
    if (groupRow) {
      userService.GetUsersListByGroupId(groupRow.id).then(function (response) {
        console.log("userService:GetUsersListByGroupId() ok");
        if (response.success && response.users) {
          setUsers(response.users);
        }
      });
      if (groupRow.permissions) setAcls(groupRow.permissions);
      orderService.GetCurrencies().then(function (response) {
        if (response.success) setCurrencies(response.currencies);
      });
      ingestService.GetProviders().then(function (response) {
        if (response.success) setLabProviders(response.providers);
      });
      userService.GetEntitiesList().then(function (response) {
        if (response.success) setEntities(response.entities);
      });
      planningUserTaskService
        .GetResourceList()
        .then((response) => setAvailablePlanningResources(response.resources || []));
    }
  }, [groupRow?.id]);

  const onClickCopyPermissionsValidationRole = (aclsToSet: AclTypes[] | null | undefined) => {
    if (aclsToSet) setAcls(aclsToSet);
    setPermissionAnchorRole(undefined);
  };

  const onClickCopyPermissionsValidationGroup = (aclsToSet: AclTypes[] | null | undefined) => {
    if (aclsToSet) setAcls(aclsToSet);
    setPermissionAnchorGroup(undefined);
  };

  // #region Views
  const handleCreateNewResourceView = () => {
    if (!openModal) return;
    openModal<ModalCreateOrUpdateResourceViewElementProps>(ModalCreateOrUpdateResourceView, {
      mode: "create",
      availableResources: availablePlanningResources,
      resourceViewToUpdate: undefined,
      addResourceViewToGroup: addResourceViewToGroup,
      editResourceViewOfGroup: (_) => false,
    });
  };

  const addResourceViewToGroup = (resourceView: GroupPlanningViewInfos) => {
    if (!groupInfos!.planningInfos)
      setGroupInfos(
        produce((draft) => {
          draft!.planningInfos = {
            viewGroups: [resourceView],
            userGroups: draft?.planningInfos?.userGroups || null,
            viewableGroupIds: draft?.planningInfos?.viewableGroupIds || null,
          };
        })
      );
    else
      setGroupInfos(
        produce((draft) => {
          draft!.planningInfos!.viewGroups!.push(resourceView);
        })
      );
  };

  const editResourceViewOfGroup = (resourceView: GroupPlanningViewInfos) => {
    setGroupInfos(
      produce((draft) => {
        const indexOfResource = draft!.planningInfos!.viewGroups!.findIndex((v) => v.name === resourceView.name);
        if (indexOfResource > -1) {
          draft!.planningInfos!.viewGroups![indexOfResource].date = resourceView.date;
          draft!.planningInfos!.viewGroups![indexOfResource].resourcesIds = resourceView.resourcesIds;
        }
      })
    );
  };

  const deleteResourceView = (resourceView: GroupPlanningViewInfos) => {
    setGroupInfos(
      produce((draft) => {
        const indexOfResource = draft!.planningInfos!.viewGroups!.findIndex((v) => v.name === resourceView.name);
        if (indexOfResource > -1) draft!.planningInfos!.viewGroups!.splice(indexOfResource, 1);
      })
    );
  };

  // #endregion Views

  const handleCreateUserGroup = () => {
    if (!openModal) return;
    openModal<ModalCreateOrUpdatePlanningUserGroupElementProps>(ModalCreateOrUpdatePlanningUserGroup, {
      mode: "create",
      addUserGroupToGroup,
      editUserGroupInGroup,
    });
  };

  const addUserGroupToGroup = (userGroup: GroupPlanningUserGroupInfos) => {
    if (!groupInfos!.planningInfos || !groupInfos!.planningInfos.userGroups)
      setGroupInfos(
        produce((draft) => {
          draft!.planningInfos = {
            viewGroups: draft?.planningInfos?.viewGroups || null,
            userGroups: [userGroup],
            viewableGroupIds: draft?.planningInfos?.viewableGroupIds || null,
          };
        })
      );
    else
      setGroupInfos(
        produce((draft) => {
          draft!.planningInfos!.userGroups!.push(userGroup);
        })
      );
  };

  const editUserGroupInGroup = (userGroup: GroupPlanningUserGroupInfos) => {
    setGroupInfos(
      produce((draft) => {
        const indexOfGroup = draft!.planningInfos!.userGroups!.findIndex((g) => g.name === userGroup.name);
        if (indexOfGroup > -1) {
          draft!.planningInfos!.userGroups![indexOfGroup] = userGroup;
        }
      })
    );
  };

  const deleteUserGroupOfGroup = (userGroup: GroupPlanningUserGroupInfos) => {
    setGroupInfos(
      produce((draft) => {
        const indexOfGroup = draft!.planningInfos!.userGroups!.findIndex((g) => g.name === userGroup.name);
        if (indexOfGroup > -1) {
          draft!.planningInfos!.userGroups!.splice(indexOfGroup, 1);
        }
      })
    );
  };

  const openModalQueryBuilder = (layer: ObjectLayer, query: string | null | undefined) => {
    if (!openModal) return;
    openModal<ModalSearchQueryBuilderElementProps>(ModalSearchQueryBuilder, {
      query: query ?? undefined,
      onValidate: (newQuery: string) => {
        setGroupInfos(
          produce((draft) => {
            if (!draft!.customFilters) draft!.customFilters = [];

            if (draft!.customFilters?.find((x) => x.layer === layer))
              draft!.customFilters!.find((x) => x.layer === layer)!.query = newQuery;
            else
              draft!.customFilters?.push({
                layer: layer,
                query: newQuery,
              });
          })
        );
      },
      hideButtons: true,
    });
  };

  const updateViewableGroups = (viewableGroupIds: number[]) => {
    if (!groupInfos!.planningInfos || !groupInfos!.planningInfos.userGroups)
      setGroupInfos(
        produce((draft) => {
          draft!.planningInfos = {
            viewGroups: draft?.planningInfos?.viewGroups || null,
            userGroups: draft?.planningInfos?.userGroups || null,
            viewableGroupIds: viewableGroupIds.length > 0 ? viewableGroupIds : null,
          };
        })
      );
    else
      setGroupInfos(
        produce((draft) => {
          draft!.planningInfos!.viewableGroupIds = viewableGroupIds.length > 0 ? viewableGroupIds : null;
        })
      );
  };

  const handleGroupAssociationsInfos = (value, context) => {
    setGroupInfos(
      produce((draft) => {
        switch (context) {
          case "currency":
            draft!.currency = value ? CurrencyTypes[value] : null;
            break;
          case "labProviders":
            draft!.labProviderIds = value.map((x) => x.id);
            break;
          case "entity":
            draft!.entityId = value ? value.id : null;
            break;
        }
      })
    );
  };

  const handleGroupContextLevelSettings = (value, context) => {
    setGroupInfos(
      produce((draft) => {
        if (context === "aclWorkflowAccessLevel")
          draft!.aclWorkflowAccessLevel = value ? AclWorkflowAccessLevel[value] : null;
        else draft!.aclAssetAccessLevel = value ? AclAssetAccessLevel[value] : null;
      })
    );
  };

  const handleGroupFiltering = (value, context) => {
    setGroupInfos(
      produce((draft) => {
        switch (context) {
          case "allowedAssetsClasses":
            draft!.allowedObjectClasses = value ? value.map((x) => ObjectClass[x]) : null;
            break;
          case "filterTitleBusinessUnits":
            draft!.filterTitleBusinessUnits = value;
            break;
          case "filterAssets":
            if (!draft!.customFilters) draft!.customFilters = [];

            if (value.currentTarget && value.currentTarget.value && value.currentTarget.value !== "") {
              if (draft!.customFilters?.find((x) => x.layer === ObjectLayer.Asset))
                draft!.customFilters!.find((x) => x.layer === ObjectLayer.Asset)!.query = value.currentTarget.value;
              else
                draft!.customFilters?.push({
                  layer: ObjectLayer.Asset,
                  query: value.currentTarget.value,
                });
            } else {
              let index = draft!.customFilters?.findIndex((x) => x.layer === ObjectLayer.Asset);
              if (index !== -1) draft!.customFilters.splice(index, 1);
            }
            break;
          case "filterContents":
            if (!draft!.customFilters) draft!.customFilters = [];

            if (value.currentTarget && value.currentTarget.value && value.currentTarget.value !== "") {
              if (draft!.customFilters?.find((x) => x.layer === ObjectLayer.Content))
                draft!.customFilters!.find((x) => x.layer === ObjectLayer.Content)!.query = value.currentTarget.value;
              else
                draft!.customFilters?.push({
                  layer: ObjectLayer.Content,
                  query: value.currentTarget.value,
                });
            } else {
              let index = draft!.customFilters?.findIndex((x) => x.layer === ObjectLayer.Content);
              if (index !== -1) draft!.customFilters.splice(index, 1);
            }
            break;
        }
      })
    );
  };

  const handleModalQB = (layer: ObjectLayer) => {
    openModalQueryBuilder(layer, groupInfos?.customFilters?.find((x) => x.layer === layer)?.query);
  };

  const handleGroupMediaShareInfos = (value, context) => {
    setGroupInfos(
      produce((draft) => {
        switch (context) {
          case "maxShareViewDays":
            draft!.maxShareViewDays = +value.target.value;
            break;
          case "maxShareViews":
            draft!.maxShareViews = +value.target.value;
            break;
          case "maxShareViewIPs":
            draft!.maxShareViewIPs = +value.target.value;
            break;
          case "maxShareViewClipsNumber":
            draft!.maxShareViewClipsNumber = +value.target.value;
            break;
          case "maxShareViewClipsDuration":
            draft!.maxShareViewClipsDuration = minutesToTimeSpan(value.target.value);
            break;
          case "defaultShareViewDays":
            draft!.defaultShareViewDays = +value.target.value;
            break;
          case "defaultShareViews":
            draft!.defaultShareViews = +value.target.value;
            break;
          case "defaultShareViewIPs":
            draft!.defaultShareViewIPs = +value.target.value;
            break;
          case "defaultShareViewClipsNumber":
            draft!.defaultShareViewClipsNumber = +value.target.value;
            break;
          case "defaultShareViewClipsDuration":
            draft!.defaultShareViewClipsDuration = minutesToTimeSpan(value.target.value);
            break;
          case "defaultShareName":
            draft!.defaultShareName = value.target.value;
            break;
        }
      })
    );
  };

  return (
    <>
      {groupInfos && (
        <Panel
          header={<PanelHeader title={t("panel:title.editGroup")} onClose={onClose as PanelHeaderProps["onClose"]} />}
          footerContent={
            <Button variant="contained" color="primary" fullWidth onClick={onClickSaveChanges}>
              {t("button:saveChanges")}
            </Button>
          }
          reload={groupInfos.id}
          open={open}
          onClose={onClose}
        >
          <Box>
            <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
              <Typography variant="h3" component="h2">
                {groupRow?.groupName}
              </Typography>
            </Stack>
            <Stack direction="row" alignItems="center" mt={2} mb={2} spacing={1.5}>
              <UserGroupChip roleId={groupRow?.roleId} roles={roles} />
            </Stack>
          </Box>
          <PanelTabs value={value} onChange={handleChange} aria-label="basic tabs example" variant="fullWidth">
            <Tab label={t("common:users")} {...a11yProps(0)} />
            <Tab label={t("common:permissions")} {...a11yProps(1)} />
            <Tab label={t("common:other")} {...a11yProps(2)} />
          </PanelTabs>
          <Box pt={3} pb={3}>
            <PanelTabContent index={0} activePanelIndex={value} id="group-user-tabpanel">
              <OutlinedInput
                label={t("field:label.filterUsers")}
                sx={{ width: "100%", mb: 2 }}
                placeholder={t("field:placeholder.filterUsersUpperCase")}
                endAdornment={
                  <InputAdornment position="end">
                    <SearchIcon />
                  </InputAdornment>
                }
                onChange={(e) => setSearchValue(e.target.value.toLowerCase())}
              />
              <Stack spacing={1.5}>
                {users
                  .filter(
                    (x) =>
                      x.userInfos !== null &&
                      (searchValue ? x.userInfos.name?.toLowerCase().includes(searchValue) : true)
                  )
                  .map((user: UserAdvancedInfos) => (
                    <Stack direction="row" justifyContent="space-between" key={user.userInfos!.id}>
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <Avatar alt={user.userInfos?.name || ""} sx={{ width: 36, height: 36 }} />
                        <Stack>
                          <Typography>
                            {user.userInfos?.firstName}{" "}
                            <Typography component="span" fontWeight={500}>
                              {user.userInfos?.lastName}
                            </Typography>
                          </Typography>
                          <Typography variant="body2" color="text.tier" sx={{ overflowWrap: "break-word" }}>
                            {user.userInfos?.email ?? ""}
                          </Typography>
                        </Stack>
                      </Stack>
                    </Stack>
                  ))}
              </Stack>
            </PanelTabContent>
            <PanelTabContent index={1} activePanelIndex={value} id="group-user-tabpanel">
              <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2} mb={3}>
                <Typography variant="h6" color="text.tier">
                  {acls.length + "/" + Object.keys(AclTypes).length}
                </Typography>
                <Button
                  size="small"
                  onClick={onClickCopyPermissionsGroup}
                  endIcon={
                    <ExpandMoreRoundedIcon
                      sx={{
                        transform: permissionAnchorGroup ? "rotate(-180deg)" : "none",
                        transition: "transform .3s",
                      }}
                    />
                  }
                >
                  {t("button:copyFromGroup")}
                </Button>
                <CopyPermissionPopperGroup
                  anchorEl={permissionAnchorGroup}
                  onClickConfirm={onClickCopyPermissionsValidationGroup}
                  onClose={() => setPermissionAnchorGroup(undefined)}
                />
                <Button
                  size="small"
                  onClick={onClickCopyPermissionsRole}
                  endIcon={
                    <ExpandMoreRoundedIcon
                      sx={{
                        transform: permissionAnchorRole ? "rotate(-180deg)" : "none",
                        transition: "transform .3s",
                      }}
                    />
                  }
                >
                  {t("button:copyFromRole")}
                </Button>
                <CopyPermissionPopperRole
                  anchorEl={permissionAnchorRole}
                  allRoles={roles}
                  onClickConfirm={onClickCopyPermissionsValidationRole}
                  onClose={() => setPermissionAnchorRole(undefined)}
                />
              </Stack>
              <OutlinedInput
                sx={{ width: "100%" }}
                label={t("common:aclFilter")}
                placeholder={t("common:aclFilter")}
                value={filterValue}
                onChange={(e) => setFilterValue(e.target.value)}
                endAdornment={
                  <InputAdornment position="end">
                    <SearchIcon />
                  </InputAdornment>
                }
              />
              <PermissionAccordionList searchValue={filterValue} acls={acls} setAcls={setAcls} />
            </PanelTabContent>
            <PanelTabContent index={2} activePanelIndex={value} id="group-user-tabpanel">
              <FormSection title={t("common:generalInformationsLowerCase")} status="todo" sx={{ mb: 2 }}>
                <Typography component="label" fontWeight={500} mb="6px">
                  {t("field:label.selectRole")}
                </Typography>
                <Select
                  displayEmpty
                  IconComponent={ExpandMoreRoundedIcon}
                  value={groupInfos.roleId || undefined}
                  sx={{ width: "100%", mb: 2 }}
                  MenuProps={{ PaperProps: { sx: { maxHeight: 600 } } }}
                  onChange={(e) => {
                    setGroupInfos(
                      produce((draft) => {
                        draft!.roleId = roles?.find((role) => role.id === +e.target.value)?.id || null;
                      })
                    );
                  }}
                >
                  <MenuItem value={undefined}>{t("menu:item.noRole")}</MenuItem>
                  {roles.map((role) => (
                    <MenuItem value={role.id} key={role.id}>
                      {role.name}
                    </MenuItem>
                  ))}
                </Select>
                <InputLabel sx={{ mb: 1 }}>{t("common:name")}</InputLabel>
                <OutlinedInput
                  value={groupInfos.name}
                  id={"name"}
                  sx={{ width: "100%", mb: 2 }}
                  onChange={(e) =>
                    setGroupInfos(
                      produce((draft) => {
                        draft!.name = e.target.value;
                      })
                    )
                  }
                />
                <InputLabel sx={{ mb: 1 }}>{t("common:description")}</InputLabel>
                <OutlinedInput
                  value={groupInfos.description}
                  id={"description"}
                  sx={{ width: "100%" }}
                  onChange={(e) =>
                    setGroupInfos(
                      produce((draft) => {
                        draft!.description = e.target.value;
                      })
                    )
                  }
                />
              </FormSection>

              <ConfigGroupAssociations
                groupInfos={groupInfos}
                titleIds={titleIds}
                currencies={currencies || null}
                labProviders={labProviders || null}
                entities={entities || null}
                onSetGroupInfos={handleGroupAssociationsInfos}
                onModalOpen={handleOpenModalTitleAssociation}
              ></ConfigGroupAssociations>

              <ConfigContextLevelSettings
                groupInfos={groupInfos}
                onSetGroupInfos={handleGroupContextLevelSettings}
              ></ConfigContextLevelSettings>

              <ConfigFiltering
                groupInfos={groupInfos}
                onSetGroupInfos={handleGroupFiltering}
                onModalQBOpen={handleModalQB}
              ></ConfigFiltering>

              <ConfigMediaShareInfos
                groupInfos={groupInfos}
                onSetGroupInfos={handleGroupMediaShareInfos}
              ></ConfigMediaShareInfos>

              <FormSection title={t("common:planning")} status="todo" sx={{ mb: 2 }}>
                <Stack spacing={2}>
                  <Stack spacing={1}>
                    <Button
                      size="small"
                      variant="contained"
                      color="primary"
                      startIcon={<AddIcon />}
                      onClick={handleCreateNewResourceView}
                    >
                      {t("button:createNewView")}
                    </Button>
                    {groupInfos && groupInfos.planningInfos && groupInfos.planningInfos.viewGroups && (
                      <Stack spacing={1} mt={2}>
                        {groupInfos.planningInfos.viewGroups.map((view, index) => (
                          <ResourceViewCard
                            key={index}
                            resources={availablePlanningResources}
                            viewInfos={view}
                            editResourceView={editResourceViewOfGroup}
                            deleteResourceView={deleteResourceView}
                          />
                        ))}
                      </Stack>
                    )}
                  </Stack>
                  <Stack spacing={1}>
                    <Button
                      size="small"
                      variant="contained"
                      color="primary"
                      startIcon={<AddIcon />}
                      onClick={handleCreateUserGroup}
                      sx={{ mb: 2 }}
                    >
                      {t("modal:title.createUserGroup")}
                    </Button>
                    {groupInfos.planningInfos?.userGroups?.map((group, index) => (
                      <PlanningUserGroupCard
                        key={index}
                        userGroup={group}
                        editUserGroupInGroup={editUserGroupInGroup}
                        deleteUserGroupOfGroup={deleteUserGroupOfGroup}
                      />
                    ))}
                  </Stack>
                  <Stack spacing={1}>
                    <InputLabel>{t("field:label.viewableGroups")}</InputLabel>
                    <Autocomplete
                      multiple
                      options={Object.keys(groupsNamesAndIds).map(Number).sort()}
                      value={groupInfos.planningInfos?.viewableGroupIds || []}
                      onChange={(_, value) => updateViewableGroups(value)}
                      getOptionLabel={(option) => groupsNamesAndIds[option]}
                      renderInput={(params) => (
                        <OutlinedInput
                          {...params.InputProps}
                          inputProps={params.inputProps}
                          placeholder={t("field:placeholder.selectViewableGroups")}
                          sx={{ width: "100%" }}
                        />
                      )}
                    />
                  </Stack>
                </Stack>
              </FormSection>
            </PanelTabContent>
          </Box>
        </Panel>
      )}
    </>
  );
};

function a11yProps(index: number) {
  return {
    id: `group-user-tab-${index}`,
    "aria-controls": `group-user-tabpanel-${index}`,
  };
}

export default GroupUserPanel;
